import { isPlatformServer } from '@angular/common'
import {
  Injectable,
  PLATFORM_ID,
  PendingTasks,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core'

import type { Observable } from 'rxjs'
import { catchError, finalize, map, of, tap, timeout } from 'rxjs'

import { ApiService } from '../api.service'
import type { AdvertisementDto, AdvertisementPlacement } from '../responses'

/**
 * Banners the server render picked, handed over to the browser so it shows the
 * same ones instead of rolling new ones and contradicting the HTML it just
 * hydrated. Kept per slot, and as a list per slot because a page is allowed to
 * hold more than one banner of the same kind.
 */
const ADVERTISEMENTS_STATE_KEY =
  makeStateKey<Record<string, (AdvertisementDto | null)[]>>('advertisements')

/**
 * How long the server render waits for a banner. A banner is decorative, so it
 * may hold up the page a little, but never for as long as the page itself is
 * worth waiting for: past this the render carries on without one and the
 * browser asks again.
 */
const SERVER_RENDER_TIMEOUT_MS = 2000

/** Identifies one banner slot across the server render and the browser. */
function slotKey(
  placement: AdvertisementPlacement,
  referenceId?: number | null
): string {
  return `${placement}:${String(referenceId ?? '')}`
}

@Injectable({
  providedIn: 'root',
})
export class AdvertisementService {
  private readonly apiService = inject(ApiService)
  private readonly platformId = inject(PLATFORM_ID)
  private readonly transferState = inject(TransferState)
  private readonly pendingTasks = inject(PendingTasks)

  /**
   * The banner to show on `placement`, or `null` when there is nothing to show.
   * That includes every failure mode: an advertisement is never worth breaking
   * a page over.
   *
   * @param placement Page type the banner sits on.
   * @param referenceId Numeric Payload id of the magazine or article being
   *   rendered. Without it only advertisements targeting _every_ magazine or
   *   article are eligible, so always pass it when it is known.
   */
  getRandom(
    placement: AdvertisementPlacement,
    referenceId?: number | null
  ): Observable<AdvertisementDto | null> {
    const slot = slotKey(placement, referenceId)

    if (isPlatformServer(this.platformId)) {
      return this.fetchForServerRender(slot, placement, referenceId)
    }

    // On the first load the server already rolled a banner for this slot and
    // rendered it, so take that one: asking for another would show something
    // the server-rendered HTML never contained. Later navigations find nothing
    // here and fetch, which is what re-rolls the banner per page view.
    const serverRendered = this.takeServerRendered(slot)

    return serverRendered === undefined
      ? this.fetch(placement, referenceId)
      : of(serverRendered)
  }

  private fetch(
    placement: AdvertisementPlacement,
    referenceId?: number | null
  ): Observable<AdvertisementDto | null> {
    return this.apiService.getRandomAdvertisement(placement, referenceId).pipe(
      map(result => result.data?.randomAdvertisement ?? null),
      catchError((error: unknown) => {
        console.error('Error fetching advertisement:', error)
        return of(null)
      })
    )
  }

  /**
   * The same fetch, but holding up the server render until it lands - without a
   * pending task the render finishes first and tears the request down - and
   * writing what came back into the state the browser picks up.
   */
  private fetchForServerRender(
    slot: string,
    placement: AdvertisementPlacement,
    referenceId?: number | null
  ): Observable<AdvertisementDto | null> {
    const taskDone = this.pendingTasks.add()

    return this.fetch(placement, referenceId).pipe(
      tap(advertisement => {
        this.keepForBrowser(slot, advertisement)
      }),
      // After the `tap`, so a timed-out slot hands nothing over and the
      // browser fetches its own banner instead of being told there is none.
      timeout({ first: SERVER_RENDER_TIMEOUT_MS, with: () => of(null) }),
      finalize(() => {
        taskDone()
      })
    )
  }

  private keepForBrowser(
    slot: string,
    advertisement: AdvertisementDto | null
  ): void {
    const state = this.transferState.get(ADVERTISEMENTS_STATE_KEY, {})

    this.transferState.set(ADVERTISEMENTS_STATE_KEY, {
      ...state,
      [slot]: [...(state[slot] ?? []), advertisement],
    })
  }

  /**
   * The banner the server rendered in this slot, or `undefined` when it did not
   * render one - a failed or timed-out fetch, or a navigation that never
   * touched the server at all.
   */
  private takeServerRendered(
    slot: string
  ): AdvertisementDto | null | undefined {
    const state = this.transferState.get(ADVERTISEMENTS_STATE_KEY, {})
    const rendered = state[slot] ?? []

    if (rendered.length === 0) return undefined

    // Taken rather than read: a second slot of the same kind gets the second
    // banner the server rolled, not this one again.
    const [advertisement, ...rest] = rendered
    this.transferState.set(ADVERTISEMENTS_STATE_KEY, { ...state, [slot]: rest })

    return advertisement
  }
}
